import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import companyUserService from '../../services/companyUserService';
import { State } from '../../state';
import './UserRegistration.css';
import { setGlobalAlert } from '../../state/action-creators/alert';

interface RegistrationForm {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  mobilePhone: string;
}

const UserRegistration: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const params = new URLSearchParams(location.search);
  const usersentityid = params.get('usersentityid');
  const token = params.get('token');

  const [form, setForm] = useState<RegistrationForm>({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    mobilePhone: '',
  });
  const [loading, setLoading] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(0);
  const [languageData, setLanguageData] = useState<any>();

  // Get language from Redux state
  const stateLanguage = useSelector((state: State) => state.languageReducer);

  useEffect(() => {
    setLanguageData(stateLanguage);
  }, [stateLanguage]);

  // Add useEffect to fetch registration details
  useEffect(() => {
    const fetchRegistrationDetails = async () => {
      if (!usersentityid || !token) return;

      try {
        const response = await companyUserService.getRegistrationDetails(usersentityid, token);
        setForm(prev => ({
          ...prev,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          username: response.data.username,
          mobilePhone: response.data.mobilePhone,
        }));
      } catch (err) {
        dispatch(setGlobalAlert(
          languageData?.data?.registration?.success?.loadError,
          'error'
        ));
      }
    };

    fetchRegistrationDetails();
  }, [usersentityid, token, languageData, dispatch]);

  useEffect(() => {
    if (redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(prev => prev - 1);
        if (redirectCountdown === 1) {
          history.push('/login');
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [redirectCountdown, history]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!usersentityid || !token) {
      dispatch(setGlobalAlert(
        languageData?.data?.registration?.success?.invalidLink,
        'error'
      ));
      setLoading(false);
      return;
    }

    try {
      await companyUserService.registerUser({
        usersEntityId: parseInt(usersentityid),
        token,
        ...form
      });
      dispatch(setGlobalAlert(
        languageData?.data?.registration?.success?.message,
        'success'
      ));
      setRedirectCountdown(5);
    } catch (err: any) {
      dispatch(setGlobalAlert(
        err.response?.data?.message || languageData?.data?.registration?.success?.failed,
        'error',
        err.response?.data?.details
      ));
    } finally {
      setLoading(false);
    }
  };

  if (!usersentityid || !token) {
    return (
      <div className="login-container">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card">
                <div className="card-body text-center">
                  <h2 className="login-title">{languageData?.data?.companyUser?.title}</h2>
                  <p className="text-danger">
                    {languageData?.data?.registration?.success?.invalidLink}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card">
              <div className="card-body">
                <div className="text-center">
                  <h2 className="login-title">{languageData?.data?.companyUser?.title}</h2>
                </div>

                {redirectCountdown > 0 && (
                  <div className="text-center mt-2">
                    {languageData?.data?.registration?.success?.countdown.replace('{seconds}', redirectCountdown.toString())}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      id="firstName"
                      placeholder={languageData?.data?.companyUser?.firstName}
                      value={form.firstName}
                      onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                    />
                    <label htmlFor="firstName">{languageData?.data?.companyUser?.firstName}</label>
                  </div>

                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      id="lastName"
                      placeholder={languageData?.data?.companyUser?.lastName}
                      value={form.lastName}
                      onChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                      required
                    />
                    <label htmlFor="lastName">{languageData?.data?.companyUser?.lastName}</label>
                  </div>

                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      id="username"
                      placeholder={languageData?.data?.companyUser?.username}
                      value={form.username}
                      onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
                      required
                    />
                    <label htmlFor="username">{languageData?.data?.companyUser?.username}</label>
                  </div>

                  <div className="form-floating mb-3">
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder={languageData?.data?.companyUser?.password}
                      value={form.password}
                      onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                    />
                    <label htmlFor="password">{languageData?.data?.companyUser?.password}</label>
                  </div>

                  <div className="form-floating mb-4">
                    <input
                      type="tel"
                      className="form-control"
                      id="mobilePhone"
                      placeholder={languageData?.data?.companyUser?.mobilePhone}
                      value={form.mobilePhone}
                      onChange={(e) => setForm(prev => ({ ...prev, mobilePhone: e.target.value }))}
                      required
                    />
                    <label htmlFor="mobilePhone">{languageData?.data?.companyUser?.mobilePhone}</label>
                  </div>

                  <button
                    className="btn btn-primary w-100 py-2"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                    )}
                    {languageData?.data?.companyUser?.buttons?.save}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegistration; 